// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import { getImageFilePath } from '@tcp/core/src/utils';
import { getIconAsPerBrand } from '@tcp/core/src/utils/utils';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty.js';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { getOrderLevelDiscount } from '@tcp/core/src/components/features/CnC/CartItemTile/molecules/CartItemTile/views/CartItemTile.utils.js';
import LoaderSkelton from '@tcp/core/src/components/common/molecules/LoaderSkelton';
import styles from '../styles/CartOfferToolTip.style';
import CartItemTileLabels, { GLOBAL_LABELS } from '../../CartItemTile/views/CartItemTile.labels';

const promotionToolTipContent = (
  currentOrderItem,
  orderPriceAdjustments = [],
  isWasPrice,
  orderLevelDiscount,
  noOfPromotionsApplied
) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  const { priceAdjustments, price, priceAfterOrderDiscount } = currentOrderItem;
  const TotalSavings = (price - priceAfterOrderDiscount)?.toFixed(2);
  const isOrderLevelPromotionApplied = orderPriceAdjustments?.length > 0 || false;
  const orderLevelPromotionText =
    isOrderLevelPromotionApplied &&
    orderPriceAdjustments.map((message) => message.itemText).join(', \n');

  return noOfPromotionsApplied > 0 ? (
    <div className="tooltip-content" isWasPrice={isWasPrice}>
      <BodyCopy
        fontSize="fs14"
        fontFamily="secondary"
        fontWeight="bold"
        className="promotions-head"
      >
        <BodyCopy
          className="no-of-coupons-applied"
          fontSize="fs12"
          fontFamily="secondary"
          fontWeight="semiBold"
        >
          <Image
            src={getIconAsPerBrand()}
            alt="Special-Offers-Icon"
            className="icon-header elem-mr-XS_6"
            width="12px"
            height="12px"
          />
          <BodyCopy
            className="inner-offer-applied"
            fontSize="fs14"
            fontFamily="secondary"
            fontWeight="semiBold"
          >
            {renderLabel(cartItemTile.lbl_item_promotion, 'lbl_item_promotion')}
          </BodyCopy>
        </BodyCopy>
      </BodyCopy>
      <BodyCopy className="list-container">
        {priceAdjustments &&
          priceAdjustments.map(
            (message) =>
              Math.abs(message.price) > 0 && (
                <BodyCopy className="promotions-applied elem-mt-XS_1" component="ul">
                  <BodyCopy
                    fontSize="fs12"
                    fontFamily="secondary"
                    fontWeight="normal"
                    component="li"
                    className="promotion-text"
                  >
                    {message.itemText}
                  </BodyCopy>
                  <BodyCopy
                    className="discount-toolTip elem-ml-MED_1"
                    fontWeight="bold"
                    fontSize="fs12"
                    fontFamily="secondary"
                  >
                    <span>-</span>
                    <span>{`$${Math.abs(message.price)?.toFixed(2)}`}</span>
                  </BodyCopy>
                </BodyCopy>
              )
          )}
      </BodyCopy>
      {isOrderLevelPromotionApplied && Math.abs(orderLevelDiscount) > 0 && (
        <>
          <BodyCopy
            className="promotions-applied elem-mt-XS_1 order-level-promotion"
            component="ul"
          >
            <BodyCopy
              fontSize="fs12"
              fontFamily="secondary"
              fontWeight="normal"
              className="promotion-text"
              component="li"
            >
              {orderLevelPromotionText}
            </BodyCopy>
            <BodyCopy
              className="discount-toolTip elem-ml-MED_1"
              fontWeight="bold"
              fontSize="fs12"
              fontFamily="secondary"
            >
              {`\u2011$${Math.abs(orderLevelDiscount)?.toFixed(2)}`}
            </BodyCopy>
          </BodyCopy>
        </>
      )}
      {noOfPromotionsApplied > 1 ? (
        <BodyCopy className="total-tool-tip elem-mt-XS_1 separator-top">
          <BodyCopy fontSize="fs12" fontFamily="secondary" fontWeight="extrabold">
            {renderLabel(cartItemTile.lbl_total_savings, 'lbl_total_savings')}
          </BodyCopy>
          <BodyCopy
            fontSize="fs12"
            fontFamily="secondary"
            fontWeight="extrabold"
            className="discount-toolTip"
          >
            {`$${TotalSavings}`}
          </BodyCopy>
        </BodyCopy>
      ) : null}
    </div>
  ) : null;
};

const getPipelineSeparator = (isPriceAvailable, isMobileWebProductTile, isSinglePageCheckout) => {
  if (isPriceAvailable && (isMobileWebProductTile || isSinglePageCheckout)) {
    return (
      <BodyCopy
        className="color-fit-size-separator-mobile"
        fontFamily="secondary"
        component="span"
        fontSize="fs13"
      >
        |
      </BodyCopy>
    );
  }
  return null;
};

// eslint-disable-next-line complexity
const CartOfferToolTip = ({
  className,
  LoyaltyPoints: LoyaltyPointsFromState = {},
  orderPriceAdjustments: orderPriceAdjustmentsFromState = {},
  allAccessoriesWithParent,
  isWasPrice,
  isPriceAvailable,
  isMobileWebProductTile,
  isSinglePageCheckout,
  isDesktopProductTileCleanUpDisabled,
  offerPriceIsLoading,
  itemOffersInfo,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  const currentOrderItem = itemOffersInfo;
  const { priceAdjustments, price, priceAfterOrderDiscount, appliedOffers } =
    currentOrderItem || {};
  const LoyaltyPoints = useMemo(
    () => JSON.parse(JSON.stringify(LoyaltyPointsFromState)),
    [LoyaltyPointsFromState]
  );
  const orderPriceAdjustments = useMemo(() => {
    const parsedOrderPriceAdjustment = JSON.parse(JSON.stringify(orderPriceAdjustmentsFromState));
    if (appliedOffers && appliedOffers.size) {
      return (
        Array.isArray(parsedOrderPriceAdjustment) &&
        parsedOrderPriceAdjustment.length > 0 &&
        parsedOrderPriceAdjustment.filter(
          (item) =>
            appliedOffers.includes(item.couponCode) || appliedOffers.includes(item.promotionId)
        )
      );
    }
    return parsedOrderPriceAdjustment;
  }, [orderPriceAdjustmentsFromState, appliedOffers]);
  const [toolTipOpen, setToolTipOpen] = useState(false);
  const totalSavings = price - priceAfterOrderDiscount;
  // Do not show the offer applied section is the total savings is zero.
  if (totalSavings <= 0) {
    return null;
  }
  const { loyaltyOrderItems = [] } = LoyaltyPoints || {};
  const priceAdjustmentsLength = (priceAdjustments && priceAdjustments?.length) || 0;
  const orderPriceAdjustmentsLength = (orderPriceAdjustments && orderPriceAdjustments?.length) || 0;
  const isOrderLevelPromotionApplied = orderPriceAdjustments?.length > 0 || false;
  const orderLevelDiscount =
    isOrderLevelPromotionApplied &&
    Math.abs(getOrderLevelDiscount(allAccessoriesWithParent, loyaltyOrderItems, currentOrderItem));

  const absOrderLevelDiscount = Math.abs(orderLevelDiscount) > 0;
  return (
    <>
      {priceAdjustmentsLength > 0 || absOrderLevelDiscount ? (
        <div className={className}>
          <div
            className={
              isPriceAvailable && (isMobileWebProductTile || isSinglePageCheckout)
                ? 'cart-offer-tool-tip'
                : ''
            }
          >
            {getPipelineSeparator(isPriceAvailable, isMobileWebProductTile, isSinglePageCheckout)}
            {!offerPriceIsLoading ? (
              <BodyCopy
                fontSize={['fs12', 'fs14', 'fs14']}
                fontFamily="secondary"
                className="offer-applied"
                fontWeight="semiBold"
                onClick={() => setToolTipOpen(!toolTipOpen)}
              >
                {`${
                  (priceAdjustmentsLength || 0) +
                  ((absOrderLevelDiscount && orderPriceAdjustmentsLength) || 0)
                } ${
                  isDesktopProductTileCleanUpDisabled
                    ? renderLabel(cartItemTile?.lbl_offer_applied, 'lbl_offer_applied')
                        ?.toString()
                        ?.toLowerCase()
                    : renderLabel(cartItemTile?.lbl_offer_applied_new, 'lbl_offer_applied')
                        ?.toString()
                        ?.toLowerCase()
                }`}
                <Image
                  src={`${getImageFilePath()}/carrot-small-down.png`}
                  alt=""
                  className={`icon-offer-carot ${toolTipOpen ? 'open' : ''}`}
                  width="10px"
                  height="10px"
                />
              </BodyCopy>
            ) : (
              <LoaderSkelton width="120px" />
            )}
          </div>
          {toolTipOpen &&
            promotionToolTipContent(
              currentOrderItem,
              orderPriceAdjustments,
              isWasPrice,
              orderLevelDiscount,
              priceAdjustmentsLength + orderPriceAdjustmentsLength
            )}
        </div>
      ) : null}
    </>
  );
};

CartOfferToolTip.defaultProps = {
  className: '',
  orderPriceAdjustments: [],
};

CartOfferToolTip.propTypes = {
  className: PropTypes.string,
  orderPriceAdjustments: PropTypes.shape([]),
};

export default withStyles(CartOfferToolTip, styles);
export { CartOfferToolTip as CartOfferToolTipVanilla };
